/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { vehicleGradesCarouselOptions } from '../options/vehicle-grades-carousel.options';

/**
 * Vehicle Grades Carousel directive
 *
 * Handles filter interactions, mobile sheets, color swatches,
 * and carousel navigation for the Vehicle Grades Carousel component.
 */
export const vehicleGradesCarouselDirective = {
  options: vehicleGradesCarouselOptions,
  instances: new Map(),

  /**
   * Initialize the directive for the given element
   *
   * @param {HTMLElement} element - Directive HTML element
   * @param {Object} customOptions - Custom options to override defaults
   */
  init(element, customOptions = {}) {
    const config = { ...vehicleGradesCarouselOptions, ...customOptions };
    const { selectors, filterCategories } = config;

    const state = {
      element,
      config,
      activeFilters: [],
      mobileSelectedFilters: [],
      swiperInstance: null,
      originalSlides: [],
    };

    // Store original slides for filtering
    const slider = element.querySelector(selectors.slider);
    if (slider) {
      state.originalSlides = Array.from(slider.querySelectorAll(selectors.card)).map(card => card.cloneNode(true));
    }

    // Setup event listeners
    this.setupFilterPills(state);
    this.setupFilterSheet(state);
    this.setupFeaturesSheet(state);
    this.setupColorSwatches(state);
    this.setupAccordions(state);
    this.setupChevrons(state);

    // Store instance
    this.instances.set(element, state);
  },

  /**
   * Get the filter category key (e.g. 'seats', 'fuelType') for a pill value, if it belongs to a radio group.
   */
  getPillGroup(filterValue, filterCategories) {
    if (!filterCategories) return null;
    for (const [key, values] of Object.entries(filterCategories)) {
      if (values.includes(filterValue)) return key;
    }
    return null;
  },

  /**
   * Get other values in the same radio group (excluding the given value).
   */
  getOthersInGroup(filterValue, filterCategories) {
    const groupKey = this.getPillGroup(filterValue, filterCategories);
    if (!groupKey) return [];
    return (filterCategories[groupKey] || []).filter(v => v !== filterValue);
  },

  /**
   * Setup desktop filter pill click handlers.
   * Pills in the same filter category (seats, fuelType) behave like radio: only one active per group.
   */
  setupFilterPills(state) {
    const { element, config } = state;
    const { selectors, filterCategories } = config;

    const pills = element.querySelectorAll(selectors.filterPill);
    const resetBtn = element.querySelector(selectors.filterReset);

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const filterValue = pill.dataset.filterValue;
        const othersInGroup = this.getOthersInGroup(filterValue, filterCategories);

        // Radio behavior: unselect other pills in the same group
        if (othersInGroup.length > 0) {
          state.activeFilters = state.activeFilters.filter(f => !othersInGroup.includes(f));
          pills.forEach(p => {
            if (othersInGroup.includes(p.dataset.filterValue)) {
              p.classList.remove('active');
            }
          });
        }

        if (pill.classList.contains('active')) {
          pill.classList.remove('active');
          state.activeFilters = state.activeFilters.filter(f => f !== filterValue);
        } else {
          pill.classList.add('active');
          state.activeFilters.push(filterValue);
        }

        this.updateResetVisibility(state);
        this.applyFilters(state);
      });
    });

    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        state.activeFilters = [];
        pills.forEach(pill => pill.classList.remove('active'));
        this.updateResetVisibility(state);
        this.applyFilters(state);
      });
    }
  },

  /**
   * Setup mobile filter sheet interactions
   */
  setupFilterSheet(state) {
    const { element, config } = state;
    const { selectors } = config;

    const openBtn = element.querySelector(selectors.filterSheetOpen);
    const closeBtns = element.querySelectorAll(selectors.filterSheetClose);
    const applyBtn = element.querySelector(selectors.filterSheetApply);
    const resetBtn = element.querySelector(selectors.filterSheetReset);
    const sheet = element.querySelector(selectors.filterSheet);
    const options = element.querySelectorAll(selectors.filterSheetOption);

    const closeFilterPopup = () => {
      sheet?.classList.remove('open');
      document.body.style.overflow = '';
    };

    if (openBtn && sheet) {
      openBtn.addEventListener('click', () => {
        state.mobileSelectedFilters = [...state.activeFilters];
        this.updateMobileFilterUI(state);
        sheet.classList.add('open');
        document.body.style.overflow = 'hidden';
      });
    }

    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeFilterPopup);
    });

    options.forEach(option => {
      option.addEventListener('click', () => {
        const filterValue = option.dataset.filterValue;
        const section = option.closest(selectors.filterSheetSection);

        if (filterValue === 'all') {
          // Clear all filters in this section
          section?.querySelectorAll(selectors.filterSheetOption).forEach(opt => {
            const val = opt.dataset.filterValue;
            if (val !== 'all') {
              state.mobileSelectedFilters = state.mobileSelectedFilters.filter(f => f !== val);
            }
          });
        } else {
          // Remove other filters from same section (single selection per section)
          section?.querySelectorAll(selectors.filterSheetOption).forEach(opt => {
            const val = opt.dataset.filterValue;
            if (val !== 'all' && val !== filterValue) {
              state.mobileSelectedFilters = state.mobileSelectedFilters.filter(f => f !== val);
            }
          });

          // Toggle the clicked filter
          const idx = state.mobileSelectedFilters.indexOf(filterValue);
          if (idx !== -1) {
            state.mobileSelectedFilters.splice(idx, 1);
          } else {
            state.mobileSelectedFilters.push(filterValue);
          }
        }

        this.updateMobileFilterUI(state);
      });
    });

    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        state.activeFilters = [...state.mobileSelectedFilters];

        // Update desktop pills
        const pills = element.querySelectorAll(selectors.filterPill);
        pills.forEach(pill => {
          const filterValue = pill.dataset.filterValue;
          pill.classList.toggle('active', state.activeFilters.includes(filterValue));
        });

        this.updateResetVisibility(state);
        this.applyFilters(state);
        closeFilterPopup();
      });
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        state.mobileSelectedFilters = [];
        state.activeFilters = [];

        // Sync desktop pills
        const pills = element.querySelectorAll(selectors.filterPill);
        pills.forEach(pill => pill.classList.remove('active'));

        this.updateMobileFilterUI(state);
        this.updateResetVisibility(state);
        this.applyFilters(state);

        sheet?.classList.remove('open');
        document.body.style.overflow = '';
      });
    }
  },

  /**
   * Setup features sheet interactions
   */
  setupFeaturesSheet(state) {
    const { element, config } = state;
    const { selectors } = config;

    const sheet = element.querySelector(selectors.featuresSheet);
    const closeBtns = element.querySelectorAll(selectors.featuresSheetClose);

    const closeFeaturesPopup = () => {
      sheet?.classList.remove('open');
      document.body.style.overflow = '';
    };

    // Use event delegation for open buttons (they're inside cards)
    element.addEventListener('click', (e) => {
      const openBtn = e.target.closest(selectors.featuresSheetOpen);
      if (openBtn && sheet) {
        e.preventDefault();
        const card = openBtn.closest(selectors.card);
        if (card) {
          this.populateFeaturesSheet(state, card);
          sheet.classList.add('open');
          document.body.style.overflow = 'hidden';
        }
      }
    });

    closeBtns.forEach(btn => {
      btn.addEventListener('click', closeFeaturesPopup);
    });
  },

  /**
   * Populate features sheet with card data
   */
  populateFeaturesSheet(state, card) {
    const { element } = state;

    const gradeDesc = card.dataset.gradeDesc || '';
    const engineHeader = card.dataset.engineHeader || '';
    const driveWheels = card.dataset.driveWheels || '';
    const seats = card.dataset.seats || '';
    const wheelSize = card.dataset.wheelSize || '';
    const price = card.dataset.price || '';
    const modelYear = card.dataset.modelYear || '';
    let additionalKeyFeatures = [];

    try {
      additionalKeyFeatures = JSON.parse(card.dataset.additionalKeyFeatures || '[]');
    } catch (e) {
      // Ignore parse error
    }

    const longDescription = card.querySelector('.vehicle-grades-carousel__card-hidden-desc')?.innerHTML || '';

    // Populate elements
    const gradeName = element.querySelector('.js-features-grade-name');
    const yearBadge = element.querySelector('.js-features-year');
    const priceValue = element.querySelector('.js-features-price');
    const pillsContainer = element.querySelector('.js-features-pills');
    const engineRow = element.querySelector('.js-features-engine-row');
    const engineValue = element.querySelector('.js-features-engine');
    const driveRow = element.querySelector('.js-features-drive-row');
    const driveValue = element.querySelector('.js-features-drive');
    const seatsRow = element.querySelector('.js-features-seats-row');
    const seatsValue = element.querySelector('.js-features-seats');
    const wheelsRow = element.querySelector('.js-features-wheels-row');
    const wheelsValue = element.querySelector('.js-features-wheels');
    const highlightsAccordion = element.querySelector('.js-highlights-accordion');
    const highlightsContent = element.querySelector('.js-features-highlights');

    if (gradeName) gradeName.textContent = gradeDesc;
    if (yearBadge) {
      yearBadge.textContent = modelYear;
      yearBadge.style.display = modelYear ? '' : 'none';
    }
    if (priceValue) priceValue.textContent = price;

    // Pills
    if (pillsContainer) {
      pillsContainer.innerHTML = '';
      additionalKeyFeatures.forEach(feature => {
        const pill = document.createElement('span');
        pill.className = 'badge badge-secondary badge-sm vehicle-grades-carousel__features-sheet-pill';
        pill.textContent = feature;
        pillsContainer.appendChild(pill);
      });
    }

    // Specs rows
    this.toggleSpecRow(engineRow, engineValue, engineHeader);
    this.toggleSpecRow(driveRow, driveValue, driveWheels);
    this.toggleSpecRow(seatsRow, seatsValue, seats);
    this.toggleSpecRow(wheelsRow, wheelsValue, wheelSize);

    // Highlights
    const trimmedDescription = longDescription.trim();
    if (highlightsAccordion) {
      highlightsAccordion.style.display = trimmedDescription ? '' : 'none';
    }
    if (highlightsContent) {
      highlightsContent.innerHTML = trimmedDescription;
    }

    this.updateFeaturesSpecDownloadLink(state, this.getSpecSheetLinkFromCard(card, state.config.selectors));
  },

  /**
   * Spec sheet link for the card's active colour variation (matches configurator variation product).
   */
  getSpecSheetLinkFromCard(card, selectors) {
    const activeSwatch = card.querySelector('.vehicle-grades-carousel__card-swatch.active')
        ?.closest(selectors.colorSwatch);

    const swatchLink = activeSwatch?.dataset.specSheetLink?.trim();
    const cardLink = card.dataset.specSheetLink?.trim();

    return [swatchLink, cardLink].find(this.isNonemptySpecSheetLink) || '';
  },

  isNonemptySpecSheetLink(link) {
    const value = link?.trim();
    return !!value && value !== '#';
  },

  updateFeaturesSpecDownloadLink(state, specSheetLink) {
      const downloadLink = state.element.querySelector(state.config.selectors.featuresSpecDownload);
      if (!downloadLink) return;

      const href = specSheetLink?.trim();

      downloadLink.hidden = !this.isNonemptySpecSheetLink(href);

      if (downloadLink.hidden) {
          downloadLink.removeAttribute('href');
          downloadLink.setAttribute('aria-hidden', 'true');
      } else {
          downloadLink.setAttribute('href', href);
          downloadLink.removeAttribute('aria-hidden');
      }
  },

  /**
   * Toggle spec row visibility
   */
  toggleSpecRow(row, valueEl, value) {
    if (row) {
      row.style.display = value ? '' : 'none';
    }
    if (valueEl) {
      valueEl.textContent = value;
    }
  },

  /**
   * Setup color swatch click handlers
   */
  setupColorSwatches(state) {
    const { element, config } = state;
    const { selectors } = config;

    element.addEventListener('click', (e) => {
      const swatch = e.target.closest(selectors.colorSwatch);
      if (!swatch) return;

      const card = swatch.closest(selectors.card);
      if (!card) return;

      const color = swatch.dataset.variationColor;
      const price = swatch.dataset.variationPrice;
      const desc = swatch.dataset.variationDesc;
      const pid = swatch.dataset.variationPid;

      // Update active swatch
      const swatchGroup = card.querySelector(selectors.colorSwatchers);
      swatchGroup?.querySelectorAll('.vehicle-grades-carousel__card-swatch').forEach(s => {
        s.classList.remove('active');
      });
      swatch.querySelector('.vehicle-grades-carousel__card-swatch')?.classList.add('active');

      // Update image
      const image = card.querySelector(selectors.cardImage);
      if (image) {
        const newSrc = image.dataset[`variationImg${this.capitalizeFirst(color)}`] ||
                       image.getAttribute(`data-variation-img-${color}`);
        if (newSrc) {
          image.src = newSrc;
        }
      }

      // Update hover image
      const hoverImage = card.querySelector(selectors.cardHoverImage);
      if (hoverImage) {
        const newHoverSrc = hoverImage.dataset[`variationHoverImg${this.capitalizeFirst(color)}`] ||
                            hoverImage.getAttribute(`data-variation-hover-img-${color}`);
        if (newHoverSrc) {
          hoverImage.src = newHoverSrc;
        }
      }

      // Update price
      const priceEl = card.querySelector(selectors.cardPrice);
      if (priceEl && price) {
        priceEl.textContent = price;
      }

      // Update description
      const descEl = card.querySelector(selectors.variantDesc);
      if (descEl) {
        descEl.innerHTML = desc || '';
      }

      const swatchSpecLink = (swatch.dataset.specSheetLink || '').trim();
      if (this.isNonemptySpecSheetLink(swatchSpecLink)) {
        card.dataset.specSheetLink = swatchSpecLink;
      } else {
        delete card.dataset.specSheetLink;
      }

      const sheet = element.querySelector(selectors.featuresSheet);
      if (sheet?.classList.contains('open')) {
        this.updateFeaturesSpecDownloadLink(state, this.getSpecSheetLinkFromCard(card, selectors));
      }

      // Update build and price CTA URL
      const ctaLink = card.querySelector(selectors.buildPriceCta);
      if (ctaLink && pid) {
        try {
          const url = new URL(ctaLink.href, window.location.origin);
          const parts = url.pathname.split('/');
          parts[parts.length - 1] = pid + '.html';
          url.pathname = parts.join('/');
          ctaLink.href = url.pathname;
        } catch (err) {
          // Ignore URL parse error
        }
      }
    });
  },

  /**
   * Setup accordion toggles
   */
  setupAccordions(state) {
    const { element, config } = state;
    const { selectors } = config;

    const toggles = element.querySelectorAll(selectors.accordionToggle);
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const accordion = toggle.closest('.vehicle-grades-carousel__features-sheet-accordion');
        accordion?.classList.toggle('open');
      });
    });
  },

  /**
   * Setup chevron navigation
   */
  setupChevrons(state) {
    const { element, config } = state;
    const { selectors } = config;

    const prevChevron = element.querySelector(selectors.chevronPrev);
    const nextChevron = element.querySelector(selectors.chevronNext);
    const slider = element.querySelector(selectors.slider);

    // Wait for Swiper to initialize
    const checkSwiper = () => {
      if (slider?.swiper) {
        state.swiperInstance = slider.swiper;
        this.updateChevronStates(state);

        slider.swiper.on('slideChange', () => {
          this.updateChevronStates(state);
        });

        return true;
      }
      return false;
    };

    if (!checkSwiper()) {
      // Retry after a short delay
      setTimeout(() => checkSwiper(), 500);
    }

    prevChevron?.addEventListener('click', () => {
      state.swiperInstance?.slidePrev();
    });

    nextChevron?.addEventListener('click', () => {
      state.swiperInstance?.slideNext();
    });
  },

  /**
   * Update chevron disabled states
   */
  updateChevronStates(state) {
    const { element, config, swiperInstance } = state;
    const { selectors, chevronColorEnabled, chevronColorDisabled } = config;

    if (!swiperInstance) return;

    const prevChevron = element.querySelector(selectors.chevronPrev);
    const nextChevron = element.querySelector(selectors.chevronNext);

    const isBeginning = swiperInstance.isBeginning;
    const isEnd = swiperInstance.isEnd;

    if (prevChevron) {
      prevChevron.classList.toggle('disabled', isBeginning);
      const prevPath = prevChevron.querySelector('path');
      if (prevPath) {
        prevPath.setAttribute('fill', isBeginning ? chevronColorDisabled : chevronColorEnabled);
      }
    }

    if (nextChevron) {
      nextChevron.classList.toggle('disabled', isEnd);
      const nextPath = nextChevron.querySelector('path');
      if (nextPath) {
        nextPath.setAttribute('fill', isEnd ? chevronColorDisabled : chevronColorEnabled);
      }
    }
  },

  /**
   * Update mobile filter sheet UI
   */
  updateMobileFilterUI(state) {
    const { element, config, mobileSelectedFilters } = state;
    const { selectors } = config;

    const options = element.querySelectorAll(selectors.filterSheetOption);

    options.forEach(option => {
      const filterValue = option.dataset.filterValue;

      if (filterValue === 'all') {
        const section = option.closest(selectors.filterSheetSection);
        let sectionHasActiveFilter = false;

        section?.querySelectorAll(selectors.filterSheetOption).forEach(opt => {
          const val = opt.dataset.filterValue;
          if (val !== 'all' && mobileSelectedFilters.includes(val)) {
            sectionHasActiveFilter = true;
          }
        });

        option.classList.toggle('active', !sectionHasActiveFilter);
      } else {
        option.classList.toggle('active', mobileSelectedFilters.includes(filterValue));
      }
    });
  },

  /**
   * Update reset link visibility
   */
  updateResetVisibility(state) {
    const { element, config, activeFilters } = state;
    const { selectors } = config;

    const resetBtn = element.querySelector(selectors.filterReset);
    if (resetBtn) {
      resetBtn.classList.toggle('visible', activeFilters.length > 0);
    }
  },

  /**
   * Apply active filters to the carousel
   */
  applyFilters(state) {
    const { element, config, activeFilters, originalSlides, swiperInstance } = state;
    const { selectors, filterCategories } = config;

    const slider = element.querySelector(selectors.slider);
    const wrapper = slider?.querySelector('.swiper-wrapper');

    // Group filters by category
    const groupedFilters = {};
    activeFilters.forEach(filterValue => {
      let category = 'other';
      for (const [cat, values] of Object.entries(filterCategories)) {
        if (values.includes(filterValue)) {
          category = cat;
          break;
        }
      }
      if (!groupedFilters[category]) {
        groupedFilters[category] = [];
      }
      groupedFilters[category].push(filterValue);
    });

    // Filter slides
    const filteredSlides = originalSlides.filter(card => {
      if (activeFilters.length === 0) return true;

      let additionalKeyFeatures = [];
      try {
        additionalKeyFeatures = JSON.parse(card.dataset.additionalKeyFeatures || '[]');
      } catch (e) {
        // Ignore parse error
      }

      // AND across categories, OR within category
      for (const [category, categoryFilters] of Object.entries(groupedFilters)) {
        const categoryMatches = categoryFilters.some(filterValue => {
          const filterLower = filterValue.toLowerCase();
          return additionalKeyFeatures.some(feature =>
            feature.toLowerCase().includes(filterLower)
          );
        });

        if (!categoryMatches) return false;
      }

      return true;
    });

    // Update Swiper slides when instance is ready
    if (swiperInstance && wrapper) {
      swiperInstance.removeAllSlides();
      filteredSlides.forEach(slide => {
        swiperInstance.appendSlide(slide.cloneNode(true));
      });

      const countEl = element.querySelector(selectors.gradesCount);
      if (countEl) {
        countEl.textContent = `(${filteredSlides.length})`;
      }

      setTimeout(() => this.updateChevronStates(state), 100);
    }

    // Show empty state when filters are applied but no variants match
    const emptyState = element.querySelector(selectors.emptyState);
    const showEmpty = activeFilters.length > 0 && filteredSlides.length === 0;
    if (emptyState) {
      emptyState.classList.toggle('d-none', !showEmpty);
    }
    if (slider) {
      slider.classList.toggle('d-none', showEmpty);
    }
  },

  /**
   * Capitalize first letter
   */
  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },

  /**
   * Destroy the directive instance
   */
  destroy(element) {
    const state = this.instances.get(element);
    if (!state) return;

    // Clean up event listeners (would need to store references for proper cleanup)
    this.instances.delete(element);
  }
};
